import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCreationRights,
  fetchUserRights,
  updateUserRights,
  type CreationRights,
  type UserRights,
  type UserRightsRecord,
} from '../api/usersApi';

const KEY = 'user-rights';

/** `enabled` is false while creating, where no account exists to read rights from. */
export const useUserRights = (id: number | null) =>
  useQuery<UserRightsRecord>({
    queryKey: [KEY, id],
    queryFn: () => fetchUserRights(id as number),
    enabled: id !== null,
  });

/** What a creation may set, read once: the answer depends on the actor, never on the form. */
export const useCreationRights = (enabled: boolean) =>
  useQuery<CreationRights>({
    queryKey: [KEY, 'creation'],
    queryFn: fetchCreationRights,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

/**
 * The write invalidates the technicians tree too: the directory shows the row this account backs,
 * and a desk moved here changes who may edit it — `canEdit` travels with every row.
 */
export const useUpdateUserRights = (id: number | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rights: UserRights) => updateUserRights(id as number, rights),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, id] });
      qc.invalidateQueries({ queryKey: ['technicians'] });
    },
  });
};
